// The week — the working altitude, in Zanetto Tools' shape (round 22).
//
// Seven equal columns inside one horizontally scrollable card, 760px minimum,
// with ONE COLOUR-FILLED TILE PER JOB: title, number, the day's aggregated
// hours, and the crew and plant split into two labelled mini-columns. This
// replaces the per-booking tinted chips the week shared with the month grid,
// which read as a roster — the same failure the agenda rework named.
//
// Two deliberate departures from the ZT original:
//   - Tiles carry `colour.barText`, never a blind `text-white` — ten of the
//     twenty-four palette fills fail 4.5:1 with white (phase 7b, measured).
//     The crew/plant divider derives from the same fact: white-text tiles
//     get a white hairline, ink-text tiles an ink one.
//   - Conflicted jobs render in the conflict red (a Dayboard extra ZT does
//     not have — its saves auto-resolve instead).
//
// At phone width this component never mounts: DM5 renders the week as the
// agenda list under 640px, and that decision is unchanged.
import { useMemo } from 'react';
import { formatClockRange, todayISO, weekdayShort, type DateOnly } from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { groupDayByJob, type JobDayGroup } from '../../lib/jobGroups';
import { nwShade, type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import { packJobLanes } from './timelineUtils';
import type { CalendarBooking, Unavailability } from '../../schedulingApi';

export function WeekView({
  days,
  byDay,
  awayByDay,
  onOpenDay,
  onAddOn,
  onSelectGroup,
  nwInfo,
  worksOn,
}: {
  days: DateOnly[];
  byDay: Map<string, CalendarBooking[]>;
  awayByDay: Map<string, Unavailability[]>;
  /** The day NUMBER opens the whole-day panel. */
  onOpenDay: (date: DateOnly) => void;
  onAddOn: (date: DateOnly) => void;
  /** A job tile opens the per-job panel — the tile is several bookings, so
      "edit this" has no single answer and the panel gives each its row. */
  onSelectGroup: (group: JobDayGroup, date: DateOnly) => void;
  nwInfo: NwLookup;
  worksOn: WorksOn;
}) {
  const today = todayISO();

  // worksOn filters BEFORE grouping: a Friday–Tuesday job does not tile onto
  // the weekend it spans, and a deliberate all-weekend job still does.
  const groupsByDay = useMemo(() => {
    const map = new Map<DateOnly, JobDayGroup[]>();
    for (const date of days) {
      const dayBookings = (byDay.get(date) ?? []).filter((b) => worksOn(b, date));
      map.set(date, groupDayByJob(dayBookings));
    }
    return map;
  }, [days, byDay, worksOn]);

  const lanes = useMemo(() => packJobLanes(groupsByDay), [groupsByDay]);

  return (
    <div className="card overflow-x-auto">
      <div className="min-w-[760px]">
        {/* Header: weekday, big day number, M/YY, and the holiday's name. */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {days.map((date) => {
            const nw = nwInfo(date);
            const isToday = date === today;
            return (
              <div
                key={date}
                className={`group border-r border-gray-200 px-2 py-2 text-center last:border-r-0 ${
                  isToday ? 'bg-brand-50' : nw ? nwShade(nw, true) : 'bg-gray-50'
                }`}
              >
                <div className="mb-0.5 text-xs font-medium text-gray-500">{weekdayShort(date)}</div>
                {/* The day number is centred like the lines above and below
                    it; the "+" is absolutely positioned so its reserved space
                    cannot shove the number off-axis. */}
                <div className="relative flex items-baseline justify-center">
                  <button
                    type="button"
                    onClick={() => onOpenDay(date)}
                    aria-label={`Everything on ${date}`}
                    className={`rounded px-1 text-lg font-bold leading-none focus:outline-none focus:ring-2 focus:ring-brand-600 ${
                      isToday ? 'text-brand-800' : 'text-gray-800'
                    }`}
                  >
                    {Number(date.slice(8, 10))}
                  </button>
                  <button
                    type="button"
                    onClick={() => onAddOn(date)}
                    aria-label={`Book something on ${date}`}
                    title="Book something on this day"
                    // Hidden until hover or focus, but a finger has no hover — coarse
                    // pointers get it outright (the ZT reorder-arrows lesson).
                    className="absolute right-0 top-1/2 -translate-y-1/2 rounded px-1 text-sm leading-none text-gray-500 opacity-0 transition-opacity focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-brand-600 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100"
                  >
                    +
                  </button>
                </div>
                <div className="text-xs text-gray-400">
                  {Number(date.slice(5, 7))}/{date.slice(2, 4)}
                </div>
                {nw?.dated && (
                  <div
                    className="mt-0.5 truncate text-[10px] font-medium leading-tight text-red-700"
                    title={nw.name ?? undefined}
                  >
                    {nw.name}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Body: one column per day, tiles in week-stable lane order. */}
        <div className="grid min-h-64 grid-cols-7">
          {days.map((date) => {
            const nw = nwInfo(date);
            const isToday = date === today;
            const groups = [...(groupsByDay.get(date) ?? [])].sort(
              (a, b) => (lanes.get(a.key) ?? 999) - (lanes.get(b.key) ?? 999)
            );
            const away = awayByDay.get(date) ?? [];

            return (
              <div
                key={date}
                className={`space-y-1 border-r border-gray-100 p-1.5 last:border-r-0 ${
                  isToday ? 'bg-brand-50/30' : nwShade(nw)
                }`}
              >
                {away.length > 0 && (
                  <div
                    className="schedule-away-strip truncate"
                    title={away.map((a) => a.reason).join(', ')}
                  >
                    {away.length} away
                  </div>
                )}

                {groups.map((group) => {
                  const colour = jobColour(group.jobNumber, group.jobId, group.inConflict);
                  // The hairlines inside a filled tile follow its text colour:
                  // a white-text tile gets white/25, an ink-text tile ink/20.
                  const divider =
                    colour.barText === 'text-white' ? 'border-white/25' : 'border-ink/20';
                  return (
                    <button
                      key={group.key}
                      type="button"
                      onClick={() => onSelectGroup(group, date)}
                      title={`${group.subtitle ?? ''} · ${group.title} · ${group.resources.join(', ')}`}
                      className={`w-full rounded px-2 py-1 text-left text-xs transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-600 ${colour.bar} ${colour.barText}`}
                    >
                      <div className="truncate font-semibold">{group.title}</div>
                      <div className="mb-1 flex items-baseline justify-between gap-3 leading-tight">
                        <span className="opacity-90">{group.subtitle}</span>
                        <span className="whitespace-nowrap tabular-nums opacity-90">
                          {formatClockRange(group.startTime, group.endTime) ?? 'All day'}
                        </span>
                      </div>
                      {(group.crew.length > 0 || group.plant.length > 0) && (
                        <div className={`mt-0.5 flex border-t pt-1 ${divider}`}>
                          {group.crew.length > 0 && (
                            <div
                              className={`flex-none ${
                                group.plant.length > 0 ? `mr-1.5 border-r pr-1.5 ${divider}` : ''
                              }`}
                            >
                              <div className="mb-0.5 text-[9px] font-semibold uppercase opacity-60">
                                Crew
                              </div>
                              {group.crew.map((name) => (
                                <div key={name} className="text-[10px] leading-snug opacity-90">
                                  {name}
                                </div>
                              ))}
                            </div>
                          )}
                          {group.plant.length > 0 && (
                            <div className="min-w-0 flex-1">
                              <div className="mb-0.5 text-[9px] font-semibold uppercase opacity-60">
                                Plant
                              </div>
                              {group.plant.map((name) => (
                                <div key={name} className="text-[10px] leading-snug opacity-90">
                                  {name}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
